<script setup lang="ts">
import { lockBodyScroll, unlockBodyScroll } from '~/utilities/ui-utilities';

interface IErrorNoticeProps {
  overlay?: boolean;
  padding?: string;
  showLogo?: boolean;
  preventScroll?: boolean;
  glowColor?: string;
  showDiamond?: boolean;
  contentClass?: string;
  iconClass?: string;
  textClass?: string;
  diamondMargin?: string;
}

const props = withDefaults(defineProps<IErrorNoticeProps>(), {
  overlay: false,
  padding: 'py-28',
  showLogo: true,
  preventScroll: true,
  showDiamond: true,
  glowColor: undefined,
  contentClass: undefined,
  iconClass: undefined,
  textClass: undefined,
  diamondMargin: '-mt-[15%]',
});

const { logoSets } = useBranding();
const router = useRouter();
const site = useSiteStore();

const defaultGlow = computed(() =>
  site.getRegion === 'ZA'
    ? 'shadow-[0px_0px_100px_3px_#005fe8]'
    : 'shadow-[0px_0px_100px_3px_#D839FF]',
);

const resolvedContentClass = computed(
  () =>
    props.contentClass ||
    (props.overlay
      ? 'w-full lg:w-2/3 max-w-[768px]'
      : 'w-full sm:w-1/2 lg:w-1/3'),
);

const resolvedIconClass = computed(
  () =>
    props.iconClass ||
    (props.overlay ? 'w-32 md:w-48 h-32 md:h-48' : 'w-24 md:w-28 h-24 md:h-28'),
);

const resolvedTextClass = computed(
  () => props.textClass || (props.overlay ? 'absolute bottom-[20%]' : ''),
);

onBeforeMount(() => {
  if (props.overlay && props.preventScroll) lockBodyScroll();
});
onBeforeUnmount(() => {
  if (props.overlay && props.preventScroll) unlockBodyScroll();
});
</script>

<template>
  <div
    class="flex flex-col items-center justify-start bg-gradient-to-b from-[#130021] to-[#14001D] text-light-50"
    :class="[
      {
        'fixed z-[15] top-0 left-0 w-screen h-screen': !!overlay,
        'h-full': !overlay,
      },
      padding,
    ]"
  >
    <div
      v-if="showLogo"
      class="shrink-0"
      :class="overlay ? 'mb-16' : 'mb-8 cursor-pointer'"
    >
      <slot name="logo">
        <DesignSystemLazyImage
          :src="logoSets.dark"
          alt="Jackpot City"
          class="w-40 md:w-56"
          fetch-priority="high"
          @click="!overlay && router.push('/')"
        />
      </slot>
    </div>
    <div
      class="flex flex-col shrink-0 items-center relative transition-all duration-300 px-4"
      :class="resolvedContentClass"
    >
      <div
        class="flex flex-col lg:flex-row items-center justify-center w-full gap-2 h-full"
      >
        <div
          class="rounded-xl bg-primitive-dark-blue-500 flex items-center justify-center relative z-[5] overflow-hidden aspect-square shrink-0"
          :class="[
            {
              [props.glowColor]: !!props.glowColor,
              [defaultGlow]: !props.glowColor,
            },
            resolvedIconClass,
          ]"
        >
          <div
            class="w-full h-full rounded-t-full opacity-20 jpc-radial-gradient absolute top-0 left-0"
          />
          <div
            class="relative z-10 flex items-center justify-center w-full h-full"
          >
            <slot name="icon" />
          </div>
        </div>
        <div v-if="$slots['icon-suffix']" class="h-full">
          <slot name="icon-suffix" />
        </div>
      </div>
      <div
        v-if="showDiamond"
        class="z-[1] w-full relative"
        :class="[diamondMargin]"
      >
        <DesignSystemIconsJPCDiamond />
      </div>
    </div>
    <div
      class="flex flex-col items-center w-full gap-4 text-center mt-8"
      :class="resolvedTextClass"
    >
      <div v-if="$slots.title" class="text-xl md:text-2xl font-bold">
        <slot name="title" />
      </div>
      <div v-if="$slots.message" class="md:text-xl">
        <slot name="message" />
      </div>
      <div
        v-if="$slots.actions"
        class="flex flex-col justify-center items-center gap-4 mt-4"
      >
        <slot name="actions" />
      </div>
      <div v-if="$slots.games" class="w-full p-2 mt-4">
        <slot name="games" />
      </div>
    </div>
  </div>
</template>
